"use client";

import { useState, useMemo } from "react";
import Link from "next/link";
import type { SeasonOverviewEntry, WeeklyScore } from "@/lib/conditions/season-overview";
import { scoreOutOf100, scoreOutOf100Label } from "@/lib/conditions/score-display";
import { SportBetaBadge } from "./SportBetaBadge";
import { conditionsSpotHref, isSailingSportKey } from "@/lib/seo/conditions-links";

type SportToggle = {
  key: string;
  label: string;
  emoji: string;
  href: string;
  isBeta?: boolean;
};

type Props = {
  entries: SeasonOverviewEntry[];
  sport: string;
  sports?: SportToggle[];
  detailBasePath: string;
};

type SortKey = "avgScore" | "currentScore";

function SeasonSportLink({ option, active }: { option: SportToggle; active: boolean }) {
  const [prefetch, setPrefetch] = useState(false);
  return (
    <Link
      href={option.href}
      scroll={false}
      prefetch={prefetch}
      onMouseEnter={() => setPrefetch(true)}
      onFocus={() => setPrefetch(true)}
      className={`inline-flex min-h-11 items-center gap-1.5 px-3.5 py-2 rounded-full text-[13px] font-medium transition-all ${active
        ? "bg-navy text-white shadow-sm"
        : "bg-white text-text-body border border-border-light hover:border-gold/30 hover:bg-gold/5"}`}
      aria-current={active ? "page" : undefined}
      aria-label={`View ${option.label}${option.isBeta ? " Beta" : ""} season overview`}
    >
      <span>{option.emoji}</span>
      <span>{option.label}</span>
      {option.isBeta && <SportBetaBadge emphasised={active} />}
    </Link>
  );
}

const MONTH_PILLS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const WEEK_MONTH_BOUNDARIES = [4, 8, 13, 17, 22, 26, 30, 35, 39, 44, 48, 53];

type ViewMode = "monthly" | "weekly";
type SeasonSafetyContext = "current" | "historical";

export type SeasonOverviewSortOptions = Readonly<{
  sport: string;
  sort: SortKey;
  selectedMonth: number | null;
  viewMode: ViewMode;
}>;

function isMeasuredScore(score: number | null, _sport: string): score is number {
  return score !== null;
}

export function weeklyMonthAverage(
  weeklyScores: WeeklyScore[],
  monthIndex: number,
  sport: string,
): number | null {
  const start = monthIndex === 0 ? 1 : WEEK_MONTH_BOUNDARIES[monthIndex - 1] + 1;
  const end = WEEK_MONTH_BOUNDARIES[monthIndex];
  const scores = weeklyScores
    .filter((w) => w.week >= start && w.week <= end)
    .map((w) => w.avgScore)
    .filter((score): score is number => isMeasuredScore(score, sport));
  if (scores.length === 0) return null;
  return scores.reduce((sum, score) => sum + score, 0) / scores.length;
}

function isWeekInMonth(week: number, monthIndex: number): boolean {
  const start = monthIndex === 0 ? 1 : WEEK_MONTH_BOUNDARIES[monthIndex - 1] + 1;
  const end = WEEK_MONTH_BOUNDARIES[monthIndex];
  return week >= start && week <= end;
}

function getMonthScore(
  entry: SeasonOverviewEntry,
  monthIndex: number,
  mode: ViewMode,
  sport: string,
): number | null {
  return mode === "monthly"
    ? entry.monthlyScores[monthIndex]?.avgScore ?? null
    : weeklyMonthAverage(entry.weeklyScores, monthIndex, sport);
}

export function seasonSafetyQualification(
  entry: Pick<SeasonOverviewEntry, "currentSafetyState" | "historicalSafetyState">,
  sport: string,
  context: SeasonSafetyContext,
): string | null {
  if (!sport.startsWith("sup-")) return null;
  const state = context === "current"
    ? entry.currentSafetyState ?? "no-data"
    : entry.historicalSafetyState ?? "no-data";
  if (state === "flagged-red") return "Red safety flag";
  if (state === "flagged-amber") return "Caution";
  if (state === "no-data") return "Safety evidence unavailable";
  return null;
}

export function sortSeasonOverviewEntries(
  entries: readonly SeasonOverviewEntry[],
  options: SeasonOverviewSortOptions,
): SeasonOverviewEntry[] {
  const { sport, sort, selectedMonth, viewMode } = options;
  const currentContext = selectedMonth === null && sort === "currentScore";
  return [...entries].sort((a, b) => {
    if (sport.startsWith("sup-") && currentContext) {
      const aEligible = a.currentSafetyState !== "flagged-red";
      const bEligible = b.currentSafetyState !== "flagged-red";
      if (aEligible !== bEligible) return bEligible ? 1 : -1;
    }

    let diff: number;
    if (selectedMonth !== null) {
      diff =
        (getMonthScore(b, selectedMonth, viewMode, sport) ?? -1) -
        (getMonthScore(a, selectedMonth, viewMode, sport) ?? -1);
    } else if (sort === "currentScore") {
      diff = (b.currentScore ?? -1) - (a.currentScore ?? -1);
    } else {
      diff = (b.avgScore ?? -1) - (a.avgScore ?? -1);
    }
    return diff || a.name.localeCompare(b.name, "en-GB");
  });
}

function weekMonthLabel(week: number): string {
  for (let i = 0; i < WEEK_MONTH_BOUNDARIES.length; i++) {
    if (week <= WEEK_MONTH_BOUNDARIES[i]) return MONTH_PILLS[i];
  }
  return "Dec";
}

function weekMonthIndex(week: number): number {
  for (let i = 0; i < WEEK_MONTH_BOUNDARIES.length; i++) {
    if (week <= WEEK_MONTH_BOUNDARIES[i]) return i;
  }
  return 11;
}

// Out-of-season glacier months render muted grey with a small green dot: the
// summit snow score is genuinely real, but the lift-served resort is shut, so the
// cell must not read as "open and great". The dot keeps the real-snow hint.
const GLACIER_DOT = "absolute top-[1px] right-[1px] w-[3px] h-[3px] rounded-full bg-emerald-500";

function isGlacierOnly(entry: SeasonOverviewEntry, score: number | null, monthIndex: number): boolean {
  return score !== null && entry.glacier && score >= 2 && (entry.outOfSeasonMonths?.[monthIndex] ?? false);
}

function computeStdDev(weeklyScores: WeeklyScore[], sport: string): number | null {
  const valid = weeklyScores
    .map((w) => w.avgScore)
    .filter((score): score is number => isMeasuredScore(score, sport));
  if (valid.length < 2) return null;
  const mean = valid.reduce((a, b) => a + b, 0) / valid.length;
  const variance = valid.reduce((sum, v) => sum + (v - mean) ** 2, 0) / valid.length;
  return Math.round(Math.sqrt(variance) * 10) / 10;
}

function variabilityLabel(sd: number): { label: string; color: string } {
  if (sd <= 1) return { label: "Steady", color: "text-emerald-600" };
  if (sd <= 2) return { label: "Variable", color: "text-amber-600" };
  return { label: "Wild", color: "text-red-500" };
}

function cellColor(score: number): string {
  if (score >= 8) return "bg-emerald-500";
  if (score >= 6) return "bg-lime-500";
  if (score >= 4) return "bg-amber-500";
  if (score >= 2) return "bg-orange-500";
  return "bg-slate-400";
}

function badgeColor(score: number): string {
  if (score >= 8) return "bg-emerald-500";
  if (score >= 6) return "bg-lime-500";
  if (score >= 4) return "bg-amber-500";
  if (score >= 2) return "bg-orange-500";
  return "bg-slate-400";
}

function scoreLabel(score: number | null): string {
  return score === null ? "Unavailable" : scoreOutOf100Label(score);
}

type SeasonCardProps = {
  entry: SeasonOverviewEntry;
  sport: string;
  detailBasePath: string;
  selectedMonth: number | null;
  viewMode: ViewMode;
  sort: SortKey;
};

function SeasonCard({ entry, sport, detailBasePath, selectedMonth, viewMode, sort }: SeasonCardProps) {
  const [expanded, setExpanded] = useState(false);
  const availableMonths = entry.monthlyScores.filter(({ avgScore }) => avgScore !== null).length;
  const safetyContext: SeasonSafetyContext = selectedMonth === null && sort === "currentScore"
    ? "current"
    : "historical";
  const safetyQualification = seasonSafetyQualification(entry, sport, safetyContext);

  const displayScore =
    selectedMonth !== null
      ? getMonthScore(entry, selectedMonth, viewMode, sport)
      : sort === "currentScore"
        ? entry.currentScore
        : entry.avgScore;

  return (
    <div className="block bg-white rounded-[16px] border border-border-light p-5 transition-all hover:border-gold/30 hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)]">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <Link
            href={isSailingSportKey(sport)
              ? conditionsSpotHref(entry.slug, sport, { anchor: "season" })
              : `${detailBasePath}/${entry.slug}#sport=${sport}&season`}
            className="no-underline"
          >
            <span className="block font-serif text-[16px] font-bold text-navy leading-tight hover:text-gold transition-colors truncate">
              {entry.name}
            </span>
          </Link>
          <div className="text-[11px] text-text-muted mt-0.5">
            {entry.country} &middot; {entry.region}
          </div>
          {safetyQualification ? (
            <span className={[
              "mt-2 inline-flex w-fit rounded-full border px-2.5 py-1 text-[11px] font-semibold",
              safetyQualification === "Red safety flag"
                ? "border-red-300 bg-red-50 text-red-800"
                : safetyQualification === "Caution"
                  ? "border-amber-300 bg-amber-50 text-amber-800"
                  : "border-slate-300 bg-slate-50 text-slate-700",
            ].join(" ")}>
              {safetyQualification}
            </span>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          {displayScore != null && (
            <span
              className={`px-2 py-0.5 rounded text-[11px] font-semibold text-white ${badgeColor(displayScore)}`}
            >
              {scoreOutOf100(displayScore)}
            </span>
          )}
          {safetyContext === "current" && displayScore !== null && sport.startsWith("sup-") ? (
            <span className="max-w-24 text-right text-[10px] text-text-muted">
              {entry.currentLabel ?? "Label unavailable"}
            </span>
          ) : null}
        </div>
      </div>

      {/* Heatmap — click to expand the monthly scores; the name above navigates. */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-label={expanded ? `Hide monthly scores for ${entry.name}` : `Show monthly scores for ${entry.name}`}
        className="block w-full text-left mb-3 cursor-pointer rounded-[4px] focus:outline-none focus-visible:ring-2 focus-visible:ring-navy/40"
      >
        {expanded ? (
          <div className="grid grid-cols-12 gap-[3px]">
            {entry.monthlyScores.map((m) => {
              const glacierOnly = isGlacierOnly(entry, m.avgScore, m.month);
              return (
                <div key={m.month} className="flex flex-col items-center gap-0.5">
                  <div
                    className={`relative w-full h-8 rounded-[3px] flex items-center justify-center ${
                      glacierOnly
                        ? "bg-slate-300"
                        : m.avgScore === null
                          ? "bg-slate-100"
                          : cellColor(m.avgScore)
                    } ${
                      selectedMonth === m.month
                        ? "ring-2 ring-navy ring-offset-1"
                        : selectedMonth !== null
                          ? "opacity-40"
                          : ""
                    }`}
                    title={
                      m.avgScore === null
                        ? `${m.label}: No data`
                        : glacierOnly
                          ? `${m.label}: ${scoreOutOf100Label(m.avgScore)}; glacier skiing only (resort closed)`
                          : `${m.label}: ${scoreOutOf100Label(m.avgScore)}`
                    }
                  >
                    {glacierOnly && <span className={GLACIER_DOT} aria-hidden="true" />}
                    <span
                      className={`text-[8px] font-bold leading-none ${
                        m.avgScore === null
                          ? "text-text-muted"
                          : glacierOnly
                            ? "text-slate-700"
                            : "text-white"
                      }`}
                    >
                      {isMeasuredScore(m.avgScore, sport) ? scoreOutOf100(m.avgScore) : "–"}
                    </span>
                  </div>
                  <span className="text-[7px] text-text-muted leading-none">{m.label[0]}</span>
                </div>
              );
            })}
          </div>
        ) : viewMode === "monthly" ? (
          <div className="flex gap-[3px]">
            {entry.monthlyScores.map((m) => {
              const glacierOnly = isGlacierOnly(entry, m.avgScore, m.month);
              return (
                <div key={m.month} className="flex-1 flex flex-col items-center gap-0.5">
                  <div
                    className={`relative overflow-hidden w-full aspect-square rounded-[3px] ${
                      glacierOnly
                        ? "bg-slate-300"
                        : m.avgScore === null
                          ? "bg-slate-100"
                          : cellColor(m.avgScore)
                    } ${
                      selectedMonth === m.month
                        ? "ring-2 ring-navy ring-offset-1"
                        : selectedMonth !== null
                          ? "opacity-40"
                          : ""
                    }`}
                    title={
                      m.avgScore === null
                        ? `${m.label}: No data`
                        : glacierOnly
                          ? `${m.label}: ${scoreOutOf100Label(m.avgScore)}; glacier skiing only (resort closed)`
                          : `${m.label}: ${scoreOutOf100Label(m.avgScore)}`
                    }
                  >
                    {glacierOnly && <span className={GLACIER_DOT} aria-hidden="true" />}
                  </div>
                  <span className="text-[7px] text-text-muted leading-none">{m.label[0]}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <div>
            <div className="flex gap-[1px]">
              {entry.weeklyScores.slice(0, 52).map((w) => {
                const glacierOnly = isGlacierOnly(entry, w.avgScore, weekMonthIndex(w.week));
                return (
                  <div
                    key={w.week}
                    className={`relative overflow-hidden flex-1 h-[14px] rounded-[2px] ${
                      glacierOnly
                        ? "bg-slate-300"
                        : isMeasuredScore(w.avgScore, sport)
                          ? cellColor(w.avgScore)
                          : "bg-slate-100"
                    } ${
                      selectedMonth !== null && !isWeekInMonth(w.week, selectedMonth) ? "opacity-30" : ""
                    }`}
                    title={`Wk ${w.week} (${weekMonthLabel(w.week)}): ${
                      isMeasuredScore(w.avgScore, sport)
                        ? `${scoreOutOf100Label(w.avgScore)}${glacierOnly ? "; glacier only" : ""}`
                        : "No data"
                    }`}
                  />
                );
              })}
            </div>
            <div className="flex mt-0.5">
              {MONTH_PILLS.map((label, i) => {
                const start = i === 0 ? 0 : WEEK_MONTH_BOUNDARIES[i - 1];
                const end = WEEK_MONTH_BOUNDARIES[i];
                const width = ((end - start) / 52) * 100;
                return (
                  <span
                    key={i}
                    className="text-[7px] text-text-muted leading-none text-center"
                    style={{ width: `${width}%` }}
                  >
                    {label[0]}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </button>

      <p className="mb-2 text-[11px] text-text-muted">
        {availableMonths === 0
          ? "Historical scores unavailable"
          : `${availableMonths}/12 months available`}
      </p>
      <div className="flex flex-wrap items-center justify-between gap-1 text-[11px] text-text-muted">
        <span>{entry.bestMonths.length > 1 ? `Best recorded: ${entry.bestMonths.join(", ")}` : "More history needed to compare months"}</span>
        {(() => {
          if (selectedMonth !== null) {
            const monthScore = getMonthScore(entry, selectedMonth, viewMode, sport);
            if (monthScore === null) {
              return (
                <span className="font-semibold text-navy">
                  {MONTH_PILLS[selectedMonth]} Unavailable
                </span>
              );
            }
            const monthWeeks = entry.weeklyScores.filter((w) => isWeekInMonth(w.week, selectedMonth));
            const sd = computeStdDev(monthWeeks, sport);
            if (sd === null) return <span className="font-semibold text-navy">{MONTH_PILLS[selectedMonth]} {scoreOutOf100Label(monthScore)}</span>;
            const { label, color } = variabilityLabel(sd);
            return (
              <span className="font-semibold text-navy">
                {MONTH_PILLS[selectedMonth]} {scoreOutOf100Label(monthScore)} · <span className="font-normal">±{sd}</span>{" "}
                <span className={`font-normal ${color}`}>{label}</span>
              </span>
            );
          }
          const sd = computeStdDev(entry.weeklyScores, sport);
          if (entry.avgScore === null) {
            return <span className="font-semibold text-navy">Avg Unavailable</span>;
          }
          if (sd === null || availableMonths < 2) return <span className="font-semibold text-navy">Avg {scoreLabel(entry.avgScore)}</span>;
          const { label, color } = variabilityLabel(sd);
          return (
            <span className="font-semibold text-navy">
              Avg {scoreLabel(entry.avgScore)} · <span className="font-normal">±{sd}</span>{" "}
              <span className={`font-normal ${color}`}>{label}</span>
            </span>
          );
        })()}
      </div>
    </div>
  );
}

export function SeasonOverviewGrid({ entries, sport, sports, detailBasePath }: Props) {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortKey>("avgScore");
  const [selectedCountry, setSelectedCountry] = useState<string>("");
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("monthly");
  // Glacier-only marking is a winter-ski concept (summit snow real, lifts shut).
  const isWinterSki = sport === "piste" || sport === "off-piste" || sport === "touring";

  const countries = useMemo(() => {
    const unique = [...new Set(entries.map((e) => e.country))].sort();
    return unique;
  }, [entries]);

  const filtered = useMemo(() => {
    let list = entries;

    if (selectedCountry) {
      list = list.filter((e) => e.country === selectedCountry);
    }

    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (e) =>
          e.name.toLowerCase().includes(q) ||
          e.country.toLowerCase().includes(q) ||
          e.region.toLowerCase().includes(q)
      );
    }

    return sortSeasonOverviewEntries(list, {
      sport,
      sort,
      selectedMonth,
      viewMode,
    });
  }, [entries, search, sort, selectedCountry, selectedMonth, viewMode, sport]);

  return (
    <div>
      {sports && sports.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {sports.map((option) => <SeasonSportLink key={option.key} option={option} active={option.key === (sport === "piste" ? "ski" : sport)} />)}
        </div>
      )}

      <div className="flex flex-col gap-3 mb-3">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, country, or region..."
          aria-label="Search season locations"
          className="w-full px-4 py-2.5 rounded-[12px] border border-border bg-white text-[14px] text-text-body placeholder:text-text-muted focus:outline-none focus:border-gold/50 focus:ring-1 focus:ring-gold/30"
        />
        <div className="flex gap-2">
          <select
            aria-label="Filter by country"
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
            className="flex-1 min-w-0 px-3 py-2.5 rounded-[12px] border border-border bg-white text-[13px] text-text-body focus:outline-none focus:border-gold/50 appearance-none cursor-pointer"
          >
            <option value="">All countries</option>
            {countries.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <select
            aria-label="Sort season locations"
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
            className="flex-1 min-w-0 px-3 py-2.5 rounded-[12px] border border-border bg-white text-[13px] text-text-body focus:outline-none focus:border-gold/50 appearance-none cursor-pointer"
          >
            <option value="avgScore">Sort: Recorded avg</option>
            <option value="currentScore">Sort: Current score</option>
          </select>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mb-6">
        <div className="flex rounded-[10px] border border-border overflow-hidden mr-1">
          <button
            onClick={() => setViewMode("monthly")}
            className={`px-3 py-1.5 text-[12px] font-medium transition-colors ${
              viewMode === "monthly"
                ? "bg-navy text-white"
                : "bg-white text-text-muted hover:text-navy"
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setViewMode("weekly")}
            className={`px-3 py-1.5 text-[12px] font-medium transition-colors border-l border-border ${
              viewMode === "weekly"
                ? "bg-navy text-white"
                : "bg-white text-text-muted hover:text-navy"
            }`}
          >
            Weekly
          </button>
        </div>
        {MONTH_PILLS.map((label, i) => (
          <button
            key={i}
            onClick={() => setSelectedMonth(selectedMonth === i ? null : i)}
            className={`px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors ${
              selectedMonth === i
                ? "bg-navy text-white"
                : "bg-white text-text-muted border border-border-light hover:border-gold/30 hover:text-navy"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {filtered.length === 0 && (
        <p className="text-center text-text-muted text-[14px] py-12">
          No results for &ldquo;{search}&rdquo;
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((entry) => (
          <SeasonCard
            key={entry.slug}
            entry={entry}
            sport={sport}
            detailBasePath={detailBasePath}
            selectedMonth={selectedMonth}
            viewMode={viewMode}
            sort={sort}
          />
        ))}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mt-8 justify-center">
        {[
          { bg: "bg-emerald-500", label: "Firing (8+)" },
          { bg: "bg-lime-500", label: "Great (6-8)" },
          { bg: "bg-amber-500", label: "Good (4-6)" },
          { bg: "bg-orange-500", label: "Fair (2-4)" },
          { bg: "bg-slate-400", label: "Flat (0-2)" },
          { bg: "bg-slate-100 border border-border-light", label: "No data" },
        ].map((item) => (
          <div key={item.label} className="flex items-center gap-1.5">
            <span className={`w-3 h-3 rounded-[3px] ${item.bg}`} />
            <span className="text-[10px] text-text-muted">{item.label}</span>
          </div>
        ))}
        {isWinterSki && (
          <div className="flex items-center gap-1.5">
            <span className="relative inline-block w-3 h-3 rounded-[3px] bg-slate-300 overflow-hidden">
              <span className="absolute top-[1px] right-[1px] w-[4px] h-[4px] rounded-full bg-emerald-500" aria-hidden="true" />
            </span>
            <span className="text-[10px] text-text-muted">Glacier only (resort closed)</span>
          </div>
        )}
      </div>
    </div>
  );
}
